// Fuente UNICA de verdad de la politica de password de cara al usuario.
//
// DEBE coincidir con:
//   - el password_policy del Cognito users pool (infra/modules/cognito/main.tf):
//     minimum_length=10, require_lowercase, require_uppercase, require_numbers,
//     require_symbols=FALSE.
//   - el Zod de signup (services/auth-oidc/dbconnections-signup/handler.ts).
//
// Cualquier cambio de la policy en Cognito se replica aca para que el checklist
// en vivo, la validacion client-side y lo que efectivamente acepta el backend
// digan exactamente lo mismo. Un usuario nunca deberia pasar la validacion local
// y ser rechazado por Cognito (ni al reves).

export const PASSWORD_MIN_LENGTH = 10;

// Token de la spec `passwordrules` de Apple (Safari/iOS Keychain + 1Password lo
// leen) para que la clave GENERADA por el manager ya cumpla nuestra policy.
// `minlength` se emite aparte desde PASSWORD_MIN_LENGTH. `required: special` NO
// se lista porque Cognito tiene require_symbols=FALSE (no lo exigimos).
type PasswordRuleToken = 'upper' | 'lower' | 'digit';

export interface PasswordRule {
  id: string;
  label: string;
  test: (pw: string) => boolean;
  // Clase de caracter que representa (si aplica) para derivar `passwordrules`.
  managerToken?: PasswordRuleToken;
}

// Orden = orden de display en el checklist. Labels concretos y obvios (incluyen
// el ejemplo entre parentesis) para que un usuario apurado entienda al toque.
export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'length',
    label: `${PASSWORD_MIN_LENGTH} caracteres mínimo`,
    test: (pw) => pw.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'uppercase',
    label: 'Una mayúscula (A-Z)',
    test: (pw) => /[A-Z]/.test(pw),
    managerToken: 'upper',
  },
  {
    id: 'lowercase',
    label: 'Una minúscula (a-z)',
    test: (pw) => /[a-z]/.test(pw),
    managerToken: 'lower',
  },
  {
    id: 'number',
    label: 'Un número (0-9)',
    test: (pw) => /[0-9]/.test(pw),
    managerToken: 'digit',
  },
];

// Atributo `passwordrules` DERIVADO de PASSWORD_RULES + PASSWORD_MIN_LENGTH:
// una sola fuente de verdad, imposible que se desincronice de la policy. Se
// pone en el input de contraseña del signup (junto con autocomplete="new-password").
export const PASSWORD_RULES_ATTR: string = [
  `minlength: ${PASSWORD_MIN_LENGTH}`,
  ...PASSWORD_RULES.flatMap((r) => (r.managerToken ? [`required: ${r.managerToken}`] : [])),
].join('; ');

export interface PasswordRuleState extends PasswordRule {
  met: boolean;
}

export function evaluatePassword(pw: string): PasswordRuleState[] {
  return PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(pw) }));
}

export function isPasswordValid(pw: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(pw));
}
